import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './VendorPage.css';

const VendorPage = () => {
  const navigate = useNavigate();

  // ==========================================
  // 🔒 HIDDEN CONSTANT (Do not display on page)
  // ==========================================
  const EVENT_NAME = "StoryFest 2026";

  // Form State
  const [formData, setFormData] = useState({
    vendorName: '',
    vendorTelephone: '',
    email: '',
    location: '',
    itemsString: '' // We'll use a string in the input, then convert to array
  });

  // UI State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Handle Input Changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    // Convert the comma-separated string into a clean array of strings
    const itemsArray = formData.itemsString
      .split(',')
      .map(item => item.trim())
      .filter(item => item !== ''); // Remove empty entries

    if (itemsArray.length === 0) {
      setErrorMessage('Please list at least one item you are bringing.');
      setIsSubmitting(false);
      return;
    }

    // Combine visible form data with hidden constants
    const payload = {
      vendorName: formData.vendorName,
      vendorTelephone: formData.vendorTelephone,
      email: formData.email,
      location: formData.location,
      items: itemsArray,
      eventName: EVENT_NAME
    };

    try {
      const response = await fetch('https://deeppink-turkey-495727.hostingersite.com/api/vendors/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSuccess(true);
        setFormData({ vendorName: '', vendorTelephone: '', email: '', location: '', itemsString: '' });
      } else {
        setErrorMessage(data.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setErrorMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="storyfest-vendor-page">
      
      {/* Background Texture */}
      <div className="storyfest-vendor-bg-pattern"></div>

      {/* Navigation */}
      <div className="storyfest-vendor-nav">
        <Link to="/storyfest" className="storyfest-vendor-back-link">← Back to StoryFest</Link>
      </div>

      <div className="storyfest-vendor-container">
        
        {/* LEFT SIDE: Title & Information */}
        <div className="storyfest-vendor-info">
          <p className="storyfest-vendor-label">PARTNER WITH US</p>
          
          <h1 className="storyfest-vendor-title">
            Become a<br />
            <span className="storyfest-vendor-text-gold">Vendor.</span>
          </h1>
          
          <p className="storyfest-vendor-desc">
            Showcase your brand, craft, or food to thousands of attendees at StoryFest 2026. 
            We are looking for the best of Made-in-Ghana.
          </p>

          <div className="storyfest-vendor-details">
            <div className="storyfest-vendor-detail-item">
              <span className="storyfest-vendor-detail-icon">📍</span>
              <div>
                <p className="storyfest-vendor-detail-label">VENUE</p>
                <p className="storyfest-vendor-detail-value">Accra City Hotel, Accra, Ghana</p>
              </div>
            </div>
            <div className="storyfest-vendor-detail-item">
              <span className="storyfest-vendor-detail-icon">📅</span>
              <div>
                <p className="storyfest-vendor-detail-label">DATE</p>
                <p className="storyfest-vendor-detail-value">11th — 13th December 2026</p>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE: Registration Form */}
        <div className="storyfest-vendor-form-wrapper">
          
          {isSuccess ? (
            <div className="storyfest-vendor-success">
              <div className="storyfest-vendor-success-icon"></div>
              <h2>Application Received!</h2>
              <p>
                Thank you for applying to be a vendor at StoryFest 2026. 
                We will review your application and get back to you shortly.
              </p>
              <button 
                onClick={() => navigate('/storyfest')} 
                className="storyfest-vendor-btn-submit" 
                style={{ marginTop: '2rem' }}
              >
                Return to StoryFest
              </button>
            </div>
          ) : (
            <form className="storyfest-vendor-form" onSubmit={handleSubmit}>
              <h2 className="storyfest-vendor-form-title">Vendor Application</h2>
              <p className="storyfest-vendor-form-subtitle">Please fill in your business details below.</p>

              {errorMessage && (
                <div className="storyfest-vendor-error">
                  {errorMessage}
                </div>
              )}

              <div className="storyfest-vendor-input-group">
                <label htmlFor="vendorName">Vendor / Business Name *</label>
                <input 
                  type="text" 
                  id="vendorName" 
                  name="vendorName" 
                  value={formData.vendorName}
                  onChange={handleChange}
                  required 
                />
              </div>

              <div className="storyfest-vendor-input-group">
                <label htmlFor="vendorTelephone">Phone Number *</label>
                <input 
                  type="tel" 
                  id="vendorTelephone" 
                  name="vendorTelephone" 
                  value={formData.vendorTelephone}
                  onChange={handleChange}
                  required 
                />
              </div>

              <div className="storyfest-vendor-input-group">
                <label htmlFor="email">Email Address *</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={formData.email}
                  onChange={handleChange}
                  required 
                />
              </div>

              <div className="storyfest-vendor-input-group">
                <label htmlFor="location">Location (City / Region) *</label>
                <input 
                  type="text" 
                  id="location" 
                  name="location" 
                  value={formData.location}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="storyfest-vendor-input-group">
                <label htmlFor="itemsString">Items you are bringing * <span className="optional">(Separate with commas)</span></label>
                <input 
                  type="text" 
                  id="itemsString" 
                  name="itemsString" 
                  value={formData.itemsString}
                  onChange={handleChange}
                  placeholder="e.g. Books, Stationery, Art Prints"
                  required
                />
              </div>

              <button 
                type="submit" 
                className="storyfest-vendor-btn-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application →'}
              </button>

              <p className="storyfest-vendor-privacy">
                By applying, you agree to our vendor terms and conditions.
              </p>
            </form>
          )}

        </div>

      </div>
    </div>
  );
};

export default VendorPage;